import {
  BookOpen,
  CalendarDays,
  ChevronDown,
  Church,
  Contact,
  FileText,
  FolderKanban,
  HandCoins,
  HeartHandshake,
  Landmark,
  Layers,
  Link2,
  Mail,
  Megaphone,
  Package,
  PieChart,
  PiggyBank,
  Plus,
  Receipt,
  Scale,
  Settings2,
  Sparkles,
  Sun,
  Target,
  UsersRound,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  hasAnyPermission,
  PERMISSIONS,
  useCurrentMembership,
  type PermissionCode,
} from '@/lib/permissions';
import { ONLINE_GIVING_SETTINGS_PATH } from '@/lib/online-giving-settings';
import { useChurchStore } from '@/stores/church-store';
import { cn } from '@/utils/cn';

export type CreateAction = {
  key: string;
  label: string;
  icon: ReactNode;
  path: string;
};

export type CreateActionGroup = {
  label: string;
  items: CreateAction[];
};

type GroupDescriptor = {
  id: string;
  labelKey: string;
};

type ActionDescriptor = {
  key: string;
  groupId: string;
  labelKey: string;
  icon: ReactNode;
  path: string;
  moduleCode: string;
  permissions: readonly PermissionCode[];
};

const CREATE_GROUPS: readonly GroupDescriptor[] = [
  { id: 'people', labelKey: 'createMenu.groups.people' },
  { id: 'ministry', labelKey: 'createMenu.groups.ministry' },
  { id: 'finance', labelKey: 'createMenu.groups.finance' },
  { id: 'organisation', labelKey: 'createMenu.groups.organisation' },
  { id: 'engagement', labelKey: 'createMenu.groups.engagement' },
];

const CREATE_ACTIONS: readonly ActionDescriptor[] = [
  {
    key: 'member',
    groupId: 'people',
    labelKey: 'createMenu.actions.member',
    icon: <Contact className="size-4" />,
    path: '/people',
    moduleCode: 'people',
    permissions: [PERMISSIONS.PEOPLE_CREATE],
  },
  {
    key: 'department',
    groupId: 'people',
    labelKey: 'createMenu.actions.department',
    icon: <Layers className="size-4" />,
    path: '/people/departments',
    moduleCode: 'people',
    permissions: [PERMISSIONS.PEOPLE_MANAGE_DEPARTMENTS],
  },
  {
    key: 'group',
    groupId: 'people',
    labelKey: 'createMenu.actions.group',
    icon: <Sparkles className="size-4" />,
    path: '/groups/auto-filled',
    moduleCode: 'groups',
    permissions: [PERMISSIONS.GROUPS_CREATE],
  },
  {
    key: 'manualGroup',
    groupId: 'people',
    labelKey: 'createMenu.actions.manualGroup',
    icon: <UsersRound className="size-4" />,
    path: '/groups',
    moduleCode: 'groups',
    permissions: [PERMISSIONS.GROUPS_CREATE],
  },
  {
    key: 'committee',
    groupId: 'people',
    labelKey: 'createMenu.actions.committee',
    icon: <Landmark className="size-4" />,
    path: '/committees',
    moduleCode: 'groups',
    permissions: [PERMISSIONS.GROUPS_CREATE],
  },
  {
    key: 'service',
    groupId: 'ministry',
    labelKey: 'createMenu.actions.service',
    icon: <Church className="size-4" />,
    path: '/services',
    moduleCode: 'programs',
    permissions: [PERMISSIONS.PROGRAMS_CREATE],
  },
  {
    key: 'program',
    groupId: 'ministry',
    labelKey: 'createMenu.actions.program',
    icon: <CalendarDays className="size-4" />,
    path: '/programs',
    moduleCode: 'programs',
    permissions: [PERMISSIONS.PROGRAMS_CREATE],
  },
  {
    key: 'contribution',
    groupId: 'finance',
    labelKey: 'createMenu.actions.contribution',
    icon: <HandCoins className="size-4" />,
    path: '/giving',
    moduleCode: 'giving',
    permissions: [PERMISSIONS.GIVING_RECORD],
  },
  {
    key: 'fund',
    groupId: 'finance',
    labelKey: 'createMenu.actions.fund',
    icon: <PiggyBank className="size-4" />,
    path: '/giving/funds',
    moduleCode: 'giving',
    permissions: [PERMISSIONS.GIVING_MANAGE_FUNDS],
  },
  {
    key: 'pledge',
    groupId: 'finance',
    labelKey: 'createMenu.actions.pledge',
    icon: <Target className="size-4" />,
    path: '/projects',
    moduleCode: 'giving',
    permissions: [PERMISSIONS.GIVING_MANAGE_PLEDGES],
  },
  {
    key: 'project',
    groupId: 'finance',
    labelKey: 'createMenu.actions.project',
    icon: <FolderKanban className="size-4" />,
    path: '/projects',
    moduleCode: 'projects',
    permissions: [PERMISSIONS.PROJECTS_CREATE],
  },
  {
    key: 'expense',
    groupId: 'finance',
    labelKey: 'createMenu.actions.expense',
    icon: <Receipt className="size-4" />,
    path: '/expenses',
    moduleCode: 'spending',
    permissions: [PERMISSIONS.EXPENSES_CREATE],
  },
  {
    key: 'welfare',
    groupId: 'finance',
    labelKey: 'createMenu.actions.welfare',
    icon: <HeartHandshake className="size-4" />,
    path: '/welfare?create=1',
    moduleCode: 'welfare',
    permissions: [PERMISSIONS.WELFARE_CREATE],
  },
  {
    key: 'budget',
    groupId: 'finance',
    labelKey: 'createMenu.actions.budget',
    icon: <PieChart className="size-4" />,
    path: '/budgets',
    moduleCode: 'budgets',
    permissions: [PERMISSIONS.BUDGETS_CREATE],
  },
  {
    key: 'balanceSheetItem',
    groupId: 'finance',
    labelKey: 'createMenu.actions.balanceSheetItem',
    icon: <Scale className="size-4" />,
    path: '/financial-position?tab=accounts&create=1',
    moduleCode: 'spending',
    permissions: [PERMISSIONS.GIVING_ADJUST_BALANCE],
  },
  {
    key: 'givingLink',
    groupId: 'finance',
    labelKey: 'createMenu.actions.givingLink',
    icon: <Link2 className="size-4" />,
    path: '/payments?createLink=1',
    moduleCode: 'payments',
    permissions: [PERMISSIONS.PAYMENTS_MANAGE_LINKS],
  },
  {
    key: 'onlineGivingSetup',
    groupId: 'finance',
    labelKey: 'createMenu.actions.onlineGivingSetup',
    icon: <Settings2 className="size-4" />,
    path: ONLINE_GIVING_SETTINGS_PATH,
    moduleCode: 'payments',
    permissions: [PERMISSIONS.PAYMENTS_CONFIGURE],
  },
  {
    key: 'asset',
    groupId: 'organisation',
    labelKey: 'createMenu.actions.asset',
    icon: <Package className="size-4" />,
    path: '/assets',
    moduleCode: 'assets',
    permissions: [PERMISSIONS.ASSETS_CREATE],
  },
  {
    key: 'message',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.message',
    icon: <Mail className="size-4" />,
    path: '/messaging',
    moduleCode: 'messaging',
    permissions: [
      PERMISSIONS.MESSAGING_SEND_PUSH,
      PERMISSIONS.MESSAGING_SEND_SMS,
      PERMISSIONS.MESSAGING_SEND_EMAIL,
    ],
  },
  {
    key: 'devotional',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.devotional',
    icon: <Sun className="size-4" />,
    path: '/content/devotionals',
    moduleCode: 'content',
    permissions: [PERMISSIONS.CONTENT_CREATE],
  },
  {
    key: 'sermon',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.sermon',
    icon: <BookOpen className="size-4" />,
    path: '/content/sermons',
    moduleCode: 'content',
    permissions: [PERMISSIONS.CONTENT_CREATE],
  },
  {
    key: 'visit',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.visit',
    icon: <HeartHandshake className="size-4" />,
    path: '/pastoral/visits',
    moduleCode: 'pastoral',
    permissions: [PERMISSIONS.PASTORAL_RECORD_VISIT],
  },
  {
    key: 'document',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.document',
    icon: <FileText className="size-4" />,
    path: '/documents',
    moduleCode: 'documents',
    permissions: [PERMISSIONS.DOCUMENTS_CREATE],
  },
  {
    key: 'soulWon',
    groupId: 'engagement',
    labelKey: 'createMenu.actions.soulWon',
    icon: <Megaphone className="size-4" />,
    path: '/evangelism',
    moduleCode: 'evangelism',
    permissions: [PERMISSIONS.EVANGELISM_RECORD],
  },
];

export function useCreateActionGroups(): CreateActionGroup[] {
  const { t } = useTranslation();
  const membership = useCurrentMembership();
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);

  return CREATE_GROUPS.map((group) => {
    const items = CREATE_ACTIONS.filter((action) => action.groupId === group.id)
      .filter((action) => {
        if (!isModuleEnabled(action.moduleCode)) return false;
        return hasAnyPermission(membership, action.permissions);
      })
      .map((action) => ({
        key: action.key,
        label: t(action.labelKey),
        icon: action.icon,
        path: action.path,
      }));

    if (items.length === 0) return null;

    return {
      label: t(group.labelKey),
      items,
    };
  }).filter((group): group is CreateActionGroup => group !== null);
}

interface CreateActionMenuProps {
  compact?: boolean;
  className?: string;
  align?: 'start' | 'center' | 'end';
}

export function CreateActionMenu({
  compact = false,
  className,
  align = 'end',
}: Readonly<CreateActionMenuProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createGroups = useCreateActionGroups();

  if (createGroups.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size={compact ? 'icon' : 'sm'}
          className={cn(className)}
          aria-label={t('createMenu.label')}
        >
          <Plus className="size-4" aria-hidden />
          {!compact ? (
            <>
              {t('createMenu.label')}
              <ChevronDown className="size-4" aria-hidden />
            </>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="max-h-[60vh] w-56 overflow-y-auto">
        {createGroups.map((group, index) => (
          <div key={group.label}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((item) => (
              <DropdownMenuItem key={item.key} onClick={() => navigate(item.path)} className="gap-2">
                {item.icon}
                {item.label}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
